import type { FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'
import { ArrowDownRight, ArrowRight } from 'lucide-react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { BrandUnderline } from '@/components/generals/brand-underline'
import { HouseMark } from '@/components/generals/house-mark'
import { Magnetic } from '@/components/generals/magnetic'
import { SectionLink } from '@/components/generals/section-link'
import { HERO, QUOTE_HASH } from '@/constants/site'
import { MEDIA } from '@/constants/media'

const FULL_FRAME = 'polygon(0% 0%, 50% 0%, 100% 0%, 100% 100%, 0% 100%)'
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#%&*+/'

interface CharsProps {
    text: string
    interactive?: boolean
}

/** Splits copy into per-letter spans inside per-word masks, so letters can rise and breathe. */
const Chars: FC<CharsProps> = ({ text, interactive = true }): ReactNode => {
    const words = text.split(' ')

    return (
        <>
            {words.map((word, wordIndex) => (
                <Fragment key={`${word}-${wordIndex}`}>
                    <span className='line-mask'>
                        {word.split('').map((char, charIndex) => (
                            <span
                                key={`${char}-${charIndex}`}
                                data-char=''
                                data-proximity={interactive ? '' : undefined}
                                className='origin-bottom-left'
                            >
                                {char}
                            </span>
                        ))}
                    </span>
                    {wordIndex < words.length - 1 ? ' ' : null}
                </Fragment>
            ))}
        </>
    )
}

/** A house-shaped clip path, sized in pixels against the stage so it never stretches. */
const houseClip = (stage: HTMLElement, centerX: number, centerY: number, size: number): string => {
    const width = Math.max(stage.clientWidth, 1)
    const height = Math.max(stage.clientHeight, 1)
    const w = (size / width) * 100
    const h = (size / height) * 100
    const left = centerX - w / 2
    const right = centerX + w / 2
    const top = centerY - h / 2
    const bottom = centerY + h / 2
    const eave = top + h * 0.36

    return `polygon(${left}% ${eave}%, ${centerX}% ${top}%, ${right}% ${eave}%, ${right}% ${bottom}%, ${left}% ${bottom}%)`
}

/**
 * Chapter one. The intro's house becomes a window that opens onto the shop owner until he
 * fills the screen; the headline builds letter by letter and swells under the cursor.
 */
export const HeroSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const root = document.documentElement
        const q = gsap.utils.selector(section)
        const stage = q('[data-stage]')[0] as HTMLElement
        const frame = q('[data-photo-frame]')[0] as HTMLElement
        const settle = q('[data-photo-settle]')[0] as HTMLElement
        const eyebrow = q('[data-scramble]')[0] as HTMLElement
        const chars = q('[data-char]') as Array<HTMLElement>
        const nearChars = q('[data-proximity]') as Array<HTMLElement>
        const mm = gsap.matchMedia()

        mm.add(
            { ok: MOTION.ok, reduce: MOTION.reduce, wide: '(min-width: 768px)', fine: MOTION.fine },
            context => {
                const { ok, wide, fine } = context.conditions as { ok: boolean, reduce: boolean, wide: boolean, fine: boolean }

                if (!ok) {
                    root.classList.add('hero-ready')

                    return
                }

                const finalText = eyebrow.dataset.text ?? eyebrow.textContent ?? ''

                /* Opening choreography ------------------------------------------------ */

                gsap.set(frame, { clipPath: houseClip(stage, 50, 50, 62) })
                gsap.set(settle, { scale: 1.6, xPercent: 0 })
                gsap.set(q('[data-shade]'), { opacity: 0 })
                gsap.set(chars, { yPercent: 118, rotate: 7 })
                gsap.set(q('[data-grow]'), { '--wdth': 62 })
                gsap.set(q('[data-hero-underline] [data-underline]'), { strokeDasharray: 1, strokeDashoffset: 1 })
                gsap.set(q('[data-meta]'), { autoAlpha: 0, y: 14 })
                gsap.set(q('[data-rise]'), { autoAlpha: 0, y: 28 })
                gsap.set(q('[data-proof-rule]'), { scaleX: 0 })
                gsap.set(q('[data-proof-text]'), { autoAlpha: 0, x: -18 })
                gsap.set(q('[data-cue]'), { autoAlpha: 0 })
                root.classList.add('hero-ready')

                const scramble = { p: 0 }

                const intro = gsap.timeline({
                    delay: root.classList.contains('intro-done') ? 0.15 : 1.05,
                    defaults: { ease: 'expo.out' }
                })

                intro
                    .to(frame, { clipPath: () => houseClip(stage, wide ? 58 : 50, wide ? 44 : 42, Math.min(stage.clientWidth, stage.clientHeight) * 0.46), duration: 0.85 }, 0)
                    .to(frame, { clipPath: FULL_FRAME, duration: 1.15, ease: 'expo.inOut' }, 0.6)
                    .to(settle, { scale: wide ? 1.16 : 1.1, xPercent: wide ? 7 : 0, duration: 2.4, ease: 'power3.out' }, 0)
                    .to(q('[data-shade]'), { opacity: 1, duration: 1.3, ease: 'power2.out' }, 0.95)
                    .to(chars, { yPercent: 0, rotate: 0, duration: 1.25, stagger: 0.024 }, 1.0)
                    .to(q('[data-grow]'), { '--wdth': 125, duration: 1.3, ease: 'power3.inOut' }, 1.75)
                    .to(q('[data-hero-underline] [data-underline]'), { strokeDashoffset: 0, duration: 0.9, ease: 'power2.inOut' }, 2.35)
                    .to(q('[data-meta]'), { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.1 }, 1.15)
                    .to(scramble, {
                        p: 1,
                        duration: 1.1,
                        ease: 'none',
                        onUpdate: () => {
                            const revealed = Math.floor(scramble.p * finalText.length)

                            eyebrow.textContent = finalText
                                .split('')
                                .map((char, index) => (index < revealed || char === ' ' ? char : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]))
                                .join('')
                        },
                        onComplete: () => {
                            eyebrow.textContent = finalText
                        }
                    }, 1.2)
                    .to(q('[data-rise]'), { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.09 }, 1.85)
                    .to(q('[data-proof-rule]'), { scaleX: 1, duration: 1.1, stagger: 0.1, ease: 'expo.inOut' }, 1.9)
                    .to(q('[data-proof-text]'), { autoAlpha: 1, x: 0, duration: 0.9, stagger: 0.1 }, 2.15)
                    .to(q('[data-cue]'), { autoAlpha: 1, duration: 0.8, ease: 'power2.out' }, 2.7)

                /* Scroll: push into the photo, drift the lines apart, fade to ink ---- */

                gsap.set(section, { height: '200vh' })
                gsap.set(stage, { position: 'sticky', top: 0 })

                gsap.timeline({
                    defaults: { ease: 'none' },
                    scrollTrigger: { trigger: section, start: 'top top', end: 'bottom bottom', scrub: 0.6 }
                })
                    .fromTo(q('[data-photo]'), { scale: 1 }, { scale: 1.16, yPercent: 5, duration: 1 }, 0)
                    .to(q('[data-line="1"]'), { xPercent: -12, duration: 0.7 }, 0)
                    .to(q('[data-line="2"]'), { xPercent: 8, duration: 0.7 }, 0)
                    .to(q('[data-line="3"]'), { xPercent: -6, duration: 0.7 }, 0)
                    .to(q('[data-line="4"]'), { xPercent: 14, duration: 0.7 }, 0)
                    .to(q('[data-fade-out]'), { autoAlpha: 0, y: -30, duration: 0.3 }, 0.05)
                    .to(q('[data-headline]'), { yPercent: -16, duration: 0.8 }, 0.1)
                    .to(q('[data-ink]'), { opacity: 0.45, duration: 0.3 }, 0.7)

                if (!fine) {
                    return () => {
                        eyebrow.textContent = finalText
                    }
                }

                /* Pointer: depth parallax and letters that widen near the cursor ----- */

                const photoX = gsap.quickTo(q('[data-photo-parallax]'), 'x', { duration: 1.2, ease: 'power3.out' })
                const photoY = gsap.quickTo(q('[data-photo-parallax]'), 'y', { duration: 1.2, ease: 'power3.out' })
                const typeX = gsap.quickTo(q('[data-type-parallax]'), 'x', { duration: 1, ease: 'power3.out' })
                const typeY = gsap.quickTo(q('[data-type-parallax]'), 'y', { duration: 1, ease: 'power3.out' })

                const base = 75
                const reach = 240
                const states = nearChars.map(() => ({ current: base, target: base, x: 0, y: 0 }))
                let pointer: { x: number, y: number } | null = null
                let running = false

                const measure = () => {
                    nearChars.forEach((char, index) => {
                        const rect = char.getBoundingClientRect()

                        states[index].x = rect.left + rect.width / 2
                        states[index].y = rect.top + rect.height / 2
                    })
                }

                const tick = () => {
                    let moving = false

                    states.forEach((state, index) => {
                        if (pointer) {
                            const distance = Math.hypot(pointer.x - state.x, pointer.y - state.y)
                            const pull = Math.max(0, 1 - distance / reach)

                            state.target = base + 50 * pull * pull
                        }
                        else {
                            state.target = base
                        }

                        const next = state.current + (state.target - state.current) * 0.14

                        if (Math.abs(next - state.current) > 0.05) moving = true

                        state.current = next
                        nearChars[index].style.setProperty('--wdth', next.toFixed(2))
                    })

                    if (!moving && !pointer) {
                        gsap.ticker.remove(tick)
                        running = false
                    }
                }

                const start = () => {
                    if (running) return

                    running = true
                    gsap.ticker.add(tick)
                }

                const onMove = (event: PointerEvent) => {
                    const rect = stage.getBoundingClientRect()
                    const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1
                    const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1

                    photoX(nx * -18)
                    photoY(ny * -12)
                    typeX(nx * 10)
                    typeY(ny * 6)

                    if (intro.progress() < 0.75) return

                    if (!pointer) measure()

                    pointer = { x: event.clientX, y: event.clientY }
                    start()
                }

                const onLeave = () => {
                    pointer = null
                    photoX(0)
                    photoY(0)
                    typeX(0)
                    typeY(0)
                    start()
                }

                stage.addEventListener('pointermove', onMove)
                stage.addEventListener('pointerleave', onLeave)
                const onScroll = () => {
                    if (pointer) measure()
                }

                window.addEventListener('resize', measure)
                window.addEventListener('scroll', onScroll, { passive: true })

                return () => {
                    stage.removeEventListener('pointermove', onMove)
                    stage.removeEventListener('pointerleave', onLeave)
                    window.removeEventListener('resize', measure)
                    window.removeEventListener('scroll', onScroll)
                    gsap.ticker.remove(tick)
                    nearChars.forEach(char => char.style.removeProperty('--wdth'))
                    eyebrow.textContent = finalText
                }
            }
        )

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='hero'
            data-tone='ink'
            data-surface='dark'
            aria-labelledby='hero-title'
            className='relative bg-ink text-paper'
        >
            <div data-stage='' className='relative h-svh min-h-[34rem] overflow-clip'>
                <div data-photo-frame='' data-hero-hide='' aria-hidden='true' className='absolute inset-0'>
                    <div data-photo-parallax='' className='absolute -inset-[3%]'>
                        <div data-photo-settle='' className='absolute inset-0 will-change-transform'>
                            <img
                                data-photo=''
                                src={MEDIA.shopOwner.src}
                                alt=''
                                width={MEDIA.shopOwner.width}
                                height={MEDIA.shopOwner.height}
                                fetchPriority='high'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover object-[60%_30%] will-change-transform'
                            />
                        </div>
                    </div>

                    <div data-shade='' className='absolute inset-0 bg-linear-to-t from-ink via-ink/40 to-ink/35' />
                    <div data-shade='' className='absolute inset-0 hidden bg-linear-to-r from-ink/90 via-ink/45 to-transparent md:block' />
                    <div data-shade='' className='grain absolute inset-0 opacity-[0.08] mix-blend-overlay' />
                </div>

                <div data-hero-hide='' className='relative z-10 flex h-full flex-col px-(--gutter) pb-[max(1.5rem,3.5vh)] pt-[calc(var(--header-h)+2vh)]'>
                    <div data-fade-out='' className='label flex items-start justify-between gap-6 text-paper/80'>
                        <span data-meta='' className='inline-flex items-center gap-3'>
                            <span aria-hidden='true' className='h-px w-8 bg-signal' />
                            <span className='sr-only'>{HERO.eyebrow}</span>
                            <span data-scramble='' data-text={HERO.eyebrow} aria-hidden='true'>{HERO.eyebrow}</span>
                        </span>

                        <span data-meta='' className='hidden text-right md:block'>
                            Web design, SEO &amp; print
                            <br />
                            Fort Worth, Texas
                        </span>
                    </div>

                    <div data-type-parallax='' className='mt-auto'>
                        <h1
                            id='hero-title'
                            data-headline=''
                            aria-label="Web design that helps your business grow."
                            className='display text-[clamp(2.9rem,13vw,4.75rem)] leading-[0.86] wdth-75 md:text-[clamp(4rem,min(8.6vw,13.5vh),12rem)]'
                        >
                            <span aria-hidden='true' className='block'>
                                <span data-line='1' className='block whitespace-nowrap'>
                                    <Chars text="Web design" />
                                </span>

                                <span data-line='2' className='block whitespace-nowrap pl-[0.6em] md:pl-[1.1em]'>
                                    <Chars text="that helps" />
                                </span>

                                <span data-line='3' className='block whitespace-nowrap'>
                                    <Chars text="your business" />
                                </span>

                                <span data-line='4' className='block whitespace-nowrap pl-[0.4em] md:pl-[1.7em]'>
                                    <span data-grow='' data-hero-underline='' className='relative inline-block text-[1.18em] leading-[0.86] text-signal'>
                                        <Chars text="grow." interactive={false} />
                                        <BrandUnderline className='-bottom-[0.08em]' />
                                    </span>
                                </span>
                            </span>
                        </h1>

                        <div data-fade-out='' className='mt-[3.5vh] grid items-end gap-x-8 gap-y-6 md:grid-cols-12'>
                            <div className='md:col-span-7 lg:col-span-5'>
                                <p data-rise='' className='max-w-[40ch] text-[0.95rem] leading-relaxed text-paper/85 md:text-lg'>
                                    {HERO.sub}
                                </p>

                                <div className='mt-5 flex flex-wrap items-center gap-x-2 gap-y-3 md:mt-6'>
                                    <div data-rise=''>
                                        <Magnetic>
                                            <SectionLink
                                                hash={QUOTE_HASH}
                                                className='group relative inline-flex h-12 items-center gap-3 overflow-hidden bg-signal px-6 font-semibold text-ink md:h-14 md:px-7'
                                            >
                                                <span aria-hidden='true' className='absolute inset-0 origin-bottom scale-y-0 bg-paper transition-transform duration-500 ease-out-expo group-hover:scale-y-100' />
                                                <span className='relative'>Get a free quote</span>
                                                <ArrowRight aria-hidden='true' className='relative size-4 transition-transform duration-300 group-hover:translate-x-1' />
                                            </SectionLink>
                                        </Magnetic>
                                    </div>

                                    <div data-rise='' className='[@media(max-height:700px)_and_(max-width:767px)]:hidden'>
                                        <SectionLink
                                            hash='services'
                                            className='group inline-flex h-12 items-center gap-3 px-4 font-semibold text-paper md:h-14'
                                        >
                                            <span className='relative'>
                                                View our services
                                                <span
                                                    aria-hidden='true'
                                                    className='absolute -bottom-1 left-0 h-px w-full origin-left bg-current transition-transform duration-500 ease-out-expo group-hover:origin-right group-hover:scale-x-0'
                                                />
                                            </span>

                                            <ArrowDownRight aria-hidden='true' className='size-4 text-signal transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5' />
                                        </SectionLink>
                                    </div>
                                </div>
                            </div>

                            <ul
                                aria-label="Results at a glance"
                                className='hidden md:col-span-5 md:col-start-8 md:block lg:col-span-4 lg:col-start-9 [@media(max-height:700px)]:md:hidden'
                            >
                                {HERO.proofs.map(proof => (
                                    <li key={proof} className='relative flex items-center gap-3 py-3 text-[0.95rem]'>
                                        <span data-proof-rule='' aria-hidden='true' className='absolute inset-x-0 top-0 h-px origin-left bg-paper/25' />
                                        <span data-proof-text='' className='flex items-center gap-3'>
                                            <HouseMark className='size-4 shrink-0' />
                                            {proof}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div
                        data-cue=''
                        aria-hidden='true'
                        className='pointer-events-none absolute bottom-[3.5vh] left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex [@media(max-height:760px)]:lg:hidden'
                    >
                        <span className='label text-[0.6rem] text-paper/60 [writing-mode:vertical-rl]'>Scroll</span>
                        <span className='relative h-12 w-px overflow-hidden bg-paper/20'>
                            <span className='hero-cue absolute inset-x-0 top-0 h-1/2 bg-signal' />
                        </span>
                    </div>
                </div>

                <div data-ink='' aria-hidden='true' className='pointer-events-none absolute inset-0 z-20 bg-ink opacity-0' />
            </div>
        </section>
    )
}
